import React from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { PageWrapper } from 'components/PageWrapper'
import { Helmet } from 'react-helmet-async'
import Home from 'views/Home'
import TermsAndConditions from 'views/TermsAndConditions'
import Rewards from 'views/Rewards'
import TransactionSuccess from 'views/TransactionSuccess'
import PrivacyPolicy from 'views/PrivacyPolicy'
import NotFound from 'views/NotFound'
import Stake from 'views/Stake'
import Exchange from 'views/Exchange'
import Licenses from 'views/Licenses'
import NodesStorage from 'views/NodesStorage'
import SharedNodeStorage from 'views/SharedNodeStorage'
import Mpro from 'views/Mpro'
import Documentation from 'views/Documentation'
import { DOCUMENTATION_DEFAULT_SLUG } from 'views/Documentation/registry'
import DAO from 'views/DAO'
import ProposalDetailView from 'views/DAO/ProposalDetailView'
import NewProposalForm from 'views/DAO/NewProposalForm'
import Marketplace from 'views/Marketplace'
import Profile from 'views/Profile'
import Community from 'views/Community'
import Notifications from 'views/Notifications'
import Terminal from 'views/Terminal'
import Stocks from 'views/Stocks'
import StockView from 'views/Stocks/StockView'
import TerminalReleaseNotes from 'views/Terminal/ReleaseNotes'
import NewsDetail from 'views/News'
import ClanView from 'views/Community/ClanView'
import ListingDetailView from 'views/Marketplace/ListingDetailView'
import NewListingForm from 'views/Marketplace/NewListingForm'
import NewOfferForm from 'views/Marketplace/NewOfferForm'
import OfferDetailView from 'views/Marketplace/OfferDetailView'
import DaoAccessGate from 'views/DAO/DaoAccessGate'

const RewardsRoute: React.FC = () => {
  const { account, isActivating } = useWeb3React()
  return (
    <PageWrapper noAccount={!account && !isActivating} loading={isActivating}>
      <Rewards />
    </PageWrapper>
  )
}

const UserLicensesRoute: React.FC = () => {
  const { account, isActivating } = useWeb3React()
  return (
    <PageWrapper noAccount={!account && !isActivating} loading={isActivating}>
      <Licenses />
    </PageWrapper>
  )
}

const NodesStorageRoute: React.FC = () => {
  const { account, isActivating } = useWeb3React()
  return (
    <PageWrapper noAccount={!account && !isActivating} loading={isActivating}>
      <NodesStorage />
    </PageWrapper>
  )
}

const TransactionSuccessRoute: React.FC = () => (
  <>
    <Helmet>
      <meta name="robots" content="noindex, follow" />
      <meta name="googlebot" content="noindex, follow" />
    </Helmet>
    <TransactionSuccess />
  </>
)

/**
 * `/exchange` moved to `/swap` (operator decision, 2026-09-02): the module holds
 * the bridge and the swap, and the swap is what people arrive for.
 *
 * The query has to survive the hop — `<Navigate to="/swap">` would drop it, and
 * every link we have handed out carries `?tab=bridge` or `?tab=swap`. Without
 * this the old bridge links would silently land on the swap tab.
 */
const ExchangeRedirect: React.FC = () => {
  const { search } = useLocation()
  return <Navigate to={`/swap${search}`} replace />
}

const AppRoutes: React.FC = () => (
  <Routes>
    <Route path="/" element={<Home />} />
    <Route path="/login" element={<Navigate to="/" replace />} />
    <Route
      path="/terms-and-conditions"
      element={
        <PageWrapper>
          <TermsAndConditions />
        </PageWrapper>
      }
    />
    <Route path="/vesting" element={<Navigate to="/user-licenses" replace />} />
    <Route path="/blog" element={<Navigate to="/" replace />} />
    <Route path="/rewards" element={<RewardsRoute />} />
    <Route path="/referrals" element={<Navigate to="/rewards" replace />} />
    <Route path="/transaction-success" element={<TransactionSuccessRoute />} />
    <Route
      path="/privacy-policy"
      element={
        <PageWrapper>
          <PrivacyPolicy />
        </PageWrapper>
      }
    />
    <Route path="/licenses/buy" element={<Navigate to="/user-licenses" replace />} />
    <Route path="/licenses" element={<Navigate to="/user-licenses" replace />} />
    <Route path="/stake" element={<Stake />} />
    {/* Scope 13 - the swap and the bridge, one module, two tabs. */}
    <Route path="/swap" element={<Exchange />} />
    <Route path="/exchange" element={<ExchangeRedirect />} />
    <Route path="/mpro" element={<Mpro />} />
    {/* Scope 40 — the tokenized-stock board and one page per company. Public
        and deliberately outside PageWrapper's wallet gate: reading a price
        needs no wallet, and these are the pages meant to be found by search. */}
    <Route path="/stocks" element={<Stocks />} />
    <Route path="/stocks/:slug" element={<StockView />} />
    {/* Redirected at the route level, deliberately outside PageWrapper: the
        wrapper pushes the current path on mount, which cancels any <Navigate>
        a page renders from inside it and leaves a blank screen. */}
    <Route path="/documentation" element={<Navigate to={`/documentation/${DOCUMENTATION_DEFAULT_SLUG}`} replace />} />
    <Route
      path="/documentation/:slug"
      element={
        <PageWrapper>
          <Documentation />
        </PageWrapper>
      }
    />
    <Route path="/user-licenses" element={<UserLicensesRoute />} />
    <Route
      path="/nodes-storage/shared"
      element={
        <PageWrapper>
          <SharedNodeStorage />
        </PageWrapper>
      }
    />
    <Route path="/nodes-storage" element={<NodesStorageRoute />} />
    {/* Phase 4 — DAO MVP. Operator decision 2026-08-30: governance is for
        licence holders, so every /dao route sits behind DaoAccessGate — a
        wallet session plus at least one licence. See ADR 0002. */}
    <Route
      path="/dao"
      element={
        <PageWrapper>
          <DaoAccessGate>
            <DAO />
          </DaoAccessGate>
        </PageWrapper>
      }
    />
    <Route
      path="/dao/proposals/new"
      element={
        <PageWrapper>
          <DaoAccessGate>
            <NewProposalForm />
          </DaoAccessGate>
        </PageWrapper>
      }
    />
    <Route
      path="/dao/proposals/:proposalId"
      element={
        <PageWrapper>
          <DaoAccessGate>
            <ProposalDetailView />
          </DaoAccessGate>
        </PageWrapper>
      }
    />
    {/* Phase 3 — NFT Marketplace MVP. Public list/detail (no wallet
        required); listing requires wallet + NFT approval; buying
        requires wallet + MPRO approval. See ADR 0003. */}
    <Route
      path="/marketplace"
      element={
        <PageWrapper>
          <Marketplace />
        </PageWrapper>
      }
    />
    <Route
      path="/profile"
      element={
        <PageWrapper>
          <Profile />
        </PageWrapper>
      }
    />
    {/* Scope 10 — the Community section. Public: everything here is either
        something its owner chose to make public or a wallet address, which is
        public by construction. Acting on a clan needs a wallet session; the
        screens say so rather than hiding. */}
    <Route
      path="/community"
      element={
        <PageWrapper>
          <Community />
        </PageWrapper>
      }
    />
    <Route
      path="/news/:id"
      element={
        <PageWrapper>
          <NewsDetail />
        </PageWrapper>
      }
    />
    <Route
      path="/notifications"
      element={
        <PageWrapper>
          <Notifications />
        </PageWrapper>
      }
    />
    {/* Terminal — the desktop app: what it is and where to get it. Public on
        purpose: the licence is checked by Terminal itself at binding, so
        reading about it and downloading it needs no wallet here. */}
    <Route
      path="/terminal"
      element={
        <PageWrapper>
          <Terminal />
        </PageWrapper>
      }
    />
    {/* Every version of Terminal, in full. Public for the same reason the
        download page is: reading about a release needs no wallet. */}
    <Route
      path="/terminal/releases"
      element={
        <PageWrapper>
          <TerminalReleaseNotes />
        </PageWrapper>
      }
    />
    <Route
      path="/community/clans/:tag"
      element={
        <PageWrapper>
          <ClanView />
        </PageWrapper>
      }
    />
    <Route
      path="/marketplace/listings/:listingId"
      element={
        <PageWrapper>
          <ListingDetailView />
        </PageWrapper>
      }
    />
    <Route
      path="/marketplace/offers/:offerId"
      element={
        <PageWrapper>
          <OfferDetailView />
        </PageWrapper>
      }
    />
    <Route
      path="/marketplace/offer"
      element={
        <PageWrapper>
          <NewOfferForm />
        </PageWrapper>
      }
    />
    <Route
      path="/marketplace/list/:nft/:tokenId"
      element={
        <PageWrapper>
          <NewListingForm />
        </PageWrapper>
      }
    />
    {/*
      The licences table links here with a collection but no token: no licence
      record carries its NFT id, so the form resolves the wallet's licences on
      chain and the seller picks one.
    */}
    <Route
      path="/marketplace/list/:nft"
      element={
        <PageWrapper>
          <NewListingForm />
        </PageWrapper>
      }
    />
    {/* The marketplace's own call to action: no collection chosen yet. */}
    <Route
      path="/marketplace/list"
      element={
        <PageWrapper>
          <NewListingForm />
        </PageWrapper>
      }
    />
    <Route path="/settings" element={<Navigate to="/user-licenses" replace />} />
    <Route path="*" element={<NotFound />} />
  </Routes>
)

export default AppRoutes
